/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { Button } from '@sonarsource/echoes-react';
import { FormattedMessage } from 'react-intl';
import { useStandardExperienceModeQuery } from '~sq-server-commons/queries/mode';
import { Component } from '~sq-server-commons/types/types';
import {
  useSandboxProjectSettingsMutation,
  useSandboxProjectSettingsQuery,
  useSandboxSettingsQuery,
} from '../../queries';
import { SandboxSettingValidationState } from '../../types';
import { useSandboxSettingForm } from '../hooks/useSandboxSettingForm';
import { InstanceDefaultPermittedQualitiesMessage } from './InstanceDefaultPermittedQualitiesMessage';
import SandboxSettingQualitiesSection from './SandboxSettingQualitiesSection';
import SandboxSettingQualitiesSectionReadonly from './SandboxSettingQualitiesSectionReadonly';
import SandboxSettingToggleSection from './SandboxSettingToggleSection';
import { SandboxSettingWrapper } from './SandboxSettingWrapper';

export function SandboxProjectSetting({ component }: Readonly<{ component: Component }>) {
  const { data: isStandardExperience } = useStandardExperienceModeQuery();
  const { data: instanceSetting, isLoading: loadingInstanceSetting } = useSandboxSettingsQuery();
  const { data: sandboxProjectSetting, isLoading: loadingProjectSetting } =
    useSandboxProjectSettingsQuery(component.key);
  const { mutate: updateSandboxSettings, isPending: isUpdating } =
    useSandboxProjectSettingsMutation(component.key);

  const {
    formState,
    validationState,
    setSoftwareQualities,
    setTypes,
    isChanged,
    reset,
    hasAddedCriticalTypesFlag,
    validate,
  } = useSandboxSettingForm(sandboxProjectSetting);

  const doSubmit = () => {
    updateSandboxSettings(
      isStandardExperience
        ? {
            types: formState.types,
          }
        : {
            softwareQualities: formState.softwareQualities,
          },
    );
  };

  const handleToggle = (enabled: boolean) => {
    updateSandboxSettings({ enabled });
  };

  const resetToDefault = () => {
    updateSandboxSettings({
      overridden: false,
    });
  };

  const resetButton = formState.enabled && sandboxProjectSetting?.overridden && (
    <Button isDisabled={isUpdating} isLoading={isUpdating} onClick={resetToDefault}>
      <FormattedMessage id="reset_to_default" />
    </Button>
  );

  if (!instanceSetting?.enabled) {
    return null;
  }

  return (
    <SandboxSettingWrapper
      additionalButtons={resetButton}
      doSubmit={doSubmit}
      hasAddedCriticalTypesFlag={hasAddedCriticalTypesFlag}
      isChanged={isChanged}
      isLoading={loadingInstanceSetting || loadingProjectSetting}
      isUpdating={isUpdating}
      onReset={reset}
      onSubmit={validate}
    >
      <SandboxSettingToggleSection
        isEnabled={formState.enabled}
        isSwitchView
        onChange={handleToggle}
      />
      {formState.enabled && (
        <div className="sw-ml-12">
          {instanceSetting?.allowOverride ? (
            <SandboxSettingQualitiesSection
              hasAddedCriticalTypes={hasAddedCriticalTypesFlag}
              isInvalid={
                validationState.softwareQualities === SandboxSettingValidationState.Invalid ||
                validationState.types === SandboxSettingValidationState.Invalid
              }
              level="project"
              onQualitiesChange={setSoftwareQualities}
              onTypesChange={setTypes}
              qualities={formState.softwareQualities}
              types={formState.types}
            />
          ) : (
            <SandboxSettingQualitiesSectionReadonly projectSettigs={sandboxProjectSetting} />
          )}
          {sandboxProjectSetting?.overridden && (
            <InstanceDefaultPermittedQualitiesMessage instanceSettings={instanceSetting} />
          )}
        </div>
      )}
    </SandboxSettingWrapper>
  );
}
